import { useMemo, useState } from "react";
import { CalendarDays, PencilLine, Shield, UserRound } from "lucide-react";

import AppShell from "@/components/AppShell";
import ProfileWorkspaceLayout from "@/components/ProfileWorkspaceLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { createRoleRequest, getMyProfile, updateMyProfile } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const EMOJI_OPTIONS = [
  "🦁", "🐯", "🐺", "🦊", "🐻", "🐼", "🦅", "🦋", "🦜", "🐬", "🐉", "🦄",
  "⚡", "🌊", "🔥", "🌙", "⭐", "🎯", "🚀", "🌿", "💎", "🏔️", "🌺", "🎸",
];

const INITIALS_PALETTES = [
  { bg: "#E0F2FE", text: "#0C4A6E" },
  { bg: "#ECFEFF", text: "#155E75" },
  { bg: "#ECFDF3", text: "#166534" },
  { bg: "#FEF9C3", text: "#854D0E" },
  { bg: "#FFF7ED", text: "#9A3412" },
  { bg: "#FCE7F3", text: "#9D174D" },
  { bg: "#EEF2FF", text: "#3730A3" },
  { bg: "#F5F3FF", text: "#6D28D9" },
  { bg: "#F3F4F6", text: "#374151" },
  { bg: "#FFE4E6", text: "#9F1239" },
  { bg: "#FEF3C7", text: "#92400E" },
  { bg: "#E2E8F0", text: "#1E293B" },
];

const roleLabelMap = {
  super_admin: "Super Admin",
  project_admin: "Project Admin",
  account_manager: "Account Manager",
  writer: "Writer",
  designer: "Designer",
  editor: "Editor",
  viewer: "Viewer",
};

const rolePermissions = {
  super_admin: [
    "Full access to all workspaces, clients and projects",
    "Can add, edit and remove members from any project",
    "Can promote or demote other admins",
    "Full HR and leave management access",
  ],
  project_admin: [
    "Can manage assigned projects and tasks",
    "Can assign work to team members",
    "Can review leaves for direct reports if set as approver",
  ],
  account_manager: [
    "Can manage client communication and tasks",
    "Can review leaves for assigned team members",
  ],
};

const requestableRoles = ["project_admin", "account_manager", "writer", "designer", "editor", "viewer"];

const initials = (value = "") => {
  const clean = String(value || "").trim();
  if (!clean) return "NA";
  const parts = clean.split(" ");
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const tenureLabel = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const today = new Date();
  const months = Math.max(0, (today.getFullYear() - date.getFullYear()) * 12 + (today.getMonth() - date.getMonth()));
  const years = Math.floor(months / 12);
  const monthName = date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  return `${monthName} (${years > 0 ? `${years}+ years` : `${months} months`})`;
};

export default function ProfilePage({ user, onLogout, onSessionUpdate }) {
  const [profile, setProfile] = useState(() => ({
    first_name: user.first_name || user.name?.split(" ")[0] || "",
    last_name: user.last_name || user.name?.split(" ").slice(1).join(" ") || "",
    email: user.email,
    role: user.role,
    workspace_domain: user.workspace_domain,
    created_at: user.created_at,
    whatsapp_country_code: user.whatsapp_country_code || "+91",
    whatsapp_number: user.whatsapp_number || "",
    birthday: user.birthday || "",
    company_joined_at: user.company_joined_at || "",
    leave_approver_email: user.approver_email || "",
    avatar_emoji: user.avatar_emoji || "",
    picture: user.picture || "",
    google_picture: user.google_picture || user.picture || "",
    picture_is_custom: Boolean(user.picture_is_custom),
    avatar_mode: user.avatar_mode || (user.avatar_emoji ? "emoji" : (user.picture ? "picture" : "initials")),
    initials_bg: user.initials_bg || INITIALS_PALETTES[0].bg,
    initials_text: user.initials_text || INITIALS_PALETTES[0].text,
  }));
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);
  const [isRoleRequestOpen, setIsRoleRequestOpen] = useState(false);
  const [requestedRole, setRequestedRole] = useState("project_admin");
  const [requestReason, setRequestReason] = useState("");
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);

  const [avatarMode, setAvatarMode] = useState(user.avatar_mode || (user.avatar_emoji ? "emoji" : (user.picture ? "picture" : "initials")));
  const [selectedEmoji, setSelectedEmoji] = useState(user.avatar_emoji || "🦁");
  const [selectedPicture, setSelectedPicture] = useState(user.picture || "");
  const [pictureSource, setPictureSource] = useState(Boolean(user.picture_is_custom) ? "upload" : "google");
  const [paletteIndex, setPaletteIndex] = useState(
    Math.max(0, INITIALS_PALETTES.findIndex((palette) => palette.bg === profile.initials_bg && palette.text === profile.initials_text)),
  );

  const displayName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || user.name || user.email;
  const activePalette = INITIALS_PALETTES[paletteIndex] || INITIALS_PALETTES[0];

  const rolePermissionRows = useMemo(
    () => rolePermissions[user.role] || ["Access is controlled by your current role assignment"],
    [user.role],
  );

  const mergedShellUser = useMemo(
    () => ({
      ...user,
      name: displayName,
      first_name: profile.first_name,
      last_name: profile.last_name,
      avatar_emoji: avatarMode === "emoji" ? selectedEmoji : null,
      picture: selectedPicture || null,
      google_picture: profile.google_picture || null,
      picture_is_custom: pictureSource === "upload" && avatarMode === "picture",
      avatar_mode: avatarMode,
      initials_bg: activePalette.bg,
      initials_text: activePalette.text,
      company_joined_at: profile.company_joined_at || user.company_joined_at,
    }),
    [activePalette.bg, activePalette.text, avatarMode, displayName, pictureSource, profile.company_joined_at, profile.first_name, profile.google_picture, profile.last_name, selectedEmoji, selectedPicture, user],
  );

  const refreshProfile = async () => {
    const response = await getMyProfile();
    setProfile({
      ...response,
      birthday: response.birthday || "",
      company_joined_at: response.company_joined_at || "",
      whatsapp_country_code: response.whatsapp_country_code || "+91",
      whatsapp_number: response.whatsapp_number || "",
      avatar_emoji: response.avatar_emoji || "",
      picture: response.picture || "",
      google_picture: response.google_picture || response.picture || "",
      picture_is_custom: Boolean(response.picture_is_custom),
      avatar_mode: response.avatar_mode || (response.avatar_emoji ? "emoji" : (response.picture ? "picture" : "initials")),
      initials_bg: response.initials_bg || activePalette.bg,
      initials_text: response.initials_text || activePalette.text,
    });
    const nextMode = response.avatar_mode || (response.avatar_emoji ? "emoji" : (response.picture ? "picture" : "initials"));
    setAvatarMode(nextMode);
    setSelectedEmoji(response.avatar_emoji || "🦁");
    setSelectedPicture(response.picture || "");
    setPictureSource(response.picture_is_custom ? "upload" : "google");
  };

  const persistSessionUser = (nextValues = {}) => {
    onSessionUpdate?.({
      ...nextValues,
      name: `${nextValues.first_name || profile.first_name || ""} ${nextValues.last_name || profile.last_name || ""}`.trim() || mergedShellUser.name,
    });
  };

  const saveAvatar = async () => {
    setIsSavingAvatar(true);
    try {
      const payload = {
        avatar_mode: avatarMode,
        avatar_emoji: avatarMode === "emoji" ? selectedEmoji : null,
        initials_bg: activePalette.bg,
        initials_text: activePalette.text,
      };
      if (avatarMode === "picture") {
        if (pictureSource === "google") {
          payload.use_google_picture = true;
        } else {
          if (!selectedPicture) {
            toast.error("Upload a photo first");
            setIsSavingAvatar(false);
            return;
          }
          payload.picture = selectedPicture || null;
          payload.picture_is_custom = true;
        }
      }
      await updateMyProfile(payload);
      persistSessionUser({
        avatar_mode: avatarMode,
        avatar_emoji: avatarMode === "emoji" ? selectedEmoji : null,
        picture: avatarMode === "picture" ? selectedPicture || null : profile.picture || null,
        picture_is_custom: avatarMode === "picture" ? pictureSource === "upload" : Boolean(profile.picture_is_custom),
        google_picture: profile.google_picture || null,
        initials_bg: activePalette.bg,
        initials_text: activePalette.text,
      });
      toast.success("Avatar updated");
      await refreshProfile();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSavingAvatar(false);
    }
  };

  const onUploadPicture = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Please upload an image smaller than 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const nextValue = String(reader.result || "");
      if (!nextValue) return;
      setAvatarMode("picture");
      setPictureSource("upload");
      setSelectedPicture(nextValue);
    };
    reader.readAsDataURL(file);
  };

  const savePersonalInfo = async () => {
    setIsSavingProfile(true);
    try {
      await updateMyProfile({
        first_name: profile.first_name,
        last_name: profile.last_name,
        whatsapp_country_code: profile.whatsapp_country_code,
        whatsapp_number: profile.whatsapp_number,
        birthday: profile.birthday || null,
        company_joined_at: profile.company_joined_at || null,
      });
      persistSessionUser({
        first_name: profile.first_name,
        last_name: profile.last_name,
        birthday: profile.birthday || null,
        company_joined_at: profile.company_joined_at || null,
        whatsapp_country_code: profile.whatsapp_country_code,
        whatsapp_number: profile.whatsapp_number,
      });
      toast.success("Profile updated");
      await refreshProfile();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const submitRoleRequest = async () => {
    if (requestReason.trim().length < 10) {
      toast.error("Please add a short reason (min 10 characters)");
      return;
    }
    setIsSubmittingRequest(true);
    try {
      await createRoleRequest({ requested_role: requestedRole, reason: requestReason.trim() });
      toast.success("Role change request submitted");
      setIsRoleRequestOpen(false);
      setRequestReason("");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  return (
    <AppShell user={mergedShellUser} onLogout={onLogout}>
      <ProfileWorkspaceLayout
        user={mergedShellUser}
        onLogout={onLogout}
        title="My Profile"
        subtitle="Update your name, avatar, contact details and confirm your birthday."
      >
        <section className="profile-card" data-testid="profile-avatar-card">
          <div className="profile-card-head">
            <div className="profile-card-title"><UserRound className="h-4 w-4" /> Avatar</div>
            <Button size="sm" variant="outline" onClick={saveAvatar} disabled={isSavingAvatar} data-testid="profile-avatar-save-button">
              {isSavingAvatar ? "Saving..." : "Save avatar"}
            </Button>
          </div>

          <div className="profile-avatar-preview" data-testid="profile-avatar-preview">
            <div
              className="profile-avatar-preview-icon"
              style={avatarMode === "emoji" || avatarMode === "picture" ? undefined : { background: activePalette.bg, color: activePalette.text }}
              data-testid="profile-avatar-preview-icon"
            >
              {avatarMode === "picture" && selectedPicture ? (
                <img
                  src={selectedPicture}
                  alt={displayName}
                  className="h-full w-full rounded-[inherit] object-cover"
                  data-testid="profile-avatar-preview-image"
                />
              ) : avatarMode === "emoji" ? selectedEmoji : initials(displayName)}
            </div>
            <div>
              <div className="profile-avatar-preview-name" data-testid="profile-avatar-preview-name">{displayName}</div>
              <div className="profile-avatar-preview-meta" data-testid="profile-avatar-preview-meta">{roleLabelMap[user.role]} · {user.workspace_domain}</div>
              <div className="profile-avatar-preview-live" data-testid="profile-avatar-preview-live">Live preview</div>
            </div>
          </div>

          <div className="profile-label">Choose an emoji</div>
          <div className="profile-emoji-grid" data-testid="profile-avatar-emoji-grid">
            {EMOJI_OPTIONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className={`profile-emoji-opt ${avatarMode === "emoji" && selectedEmoji === emoji ? "active" : ""}`}
                onClick={() => {
                  setAvatarMode("emoji");
                  setSelectedEmoji(emoji);
                }}
                data-testid={`profile-avatar-emoji-option-${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>

          <div className="profile-label">Or use initials</div>
          <div className="profile-initials-grid" data-testid="profile-avatar-initials-grid">
            {INITIALS_PALETTES.map((palette, idx) => (
              <button
                key={`${palette.bg}-${palette.text}`}
                type="button"
                className={`profile-initial-opt ${avatarMode === "initials" && paletteIndex === idx ? "active" : ""}`}
                style={{ background: palette.bg, color: palette.text }}
                onClick={() => {
                  setAvatarMode("initials");
                  setPaletteIndex(idx);
                }}
                data-testid={`profile-avatar-initial-option-${idx + 1}`}
              >
                {initials(displayName)}
              </button>
            ))}
          </div>

          <div className="profile-label">Or use a profile photo</div>
          <div className="flex flex-wrap items-center gap-2" data-testid="profile-avatar-picture-controls">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (!profile.google_picture) {
                  toast.error("No Google profile photo found for this account");
                  return;
                }
                setAvatarMode("picture");
                setPictureSource("google");
                setSelectedPicture(profile.google_picture);
              }}
              data-testid="profile-avatar-use-google-picture-button"
            >
              Use Google DP
            </Button>
            <label className="btn btn-sm" data-testid="profile-avatar-upload-label">
              Upload photo
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onUploadPicture}
                className="hidden"
                data-testid="profile-avatar-upload-input"
              />
            </label>
            {avatarMode === "picture" ? (
              <span className="chip chip-mute" data-testid="profile-avatar-picture-source-chip">
                {pictureSource === "google" ? "Google photo" : "Uploaded photo"}
              </span>
            ) : null}
          </div>
        </section>

        <section className="profile-card" data-testid="profile-personal-info-card">
          <div className="profile-card-head">
            <div className="profile-card-title"><PencilLine className="h-4 w-4" /> Personal info</div>
            <Button onClick={savePersonalInfo} disabled={isSavingProfile} data-testid="profile-personal-save-button">
              {isSavingProfile ? "Saving..." : "Save changes"}
            </Button>
          </div>

          <div className="profile-grid-two" data-testid="profile-name-grid">
            <div>
              <label className="profile-field-label" htmlFor="profile-first-name">First name *</label>
              <Input id="profile-first-name" value={profile.first_name || ""} onChange={(e) => setProfile((prev) => ({ ...prev, first_name: e.target.value }))} data-testid="profile-first-name-input" />
            </div>
            <div>
              <label className="profile-field-label" htmlFor="profile-last-name">Last name *</label>
              <Input id="profile-last-name" value={profile.last_name || ""} onChange={(e) => setProfile((prev) => ({ ...prev, last_name: e.target.value }))} data-testid="profile-last-name-input" />
            </div>
          </div>

          <div className="profile-grid-two" data-testid="profile-email-workspace-grid">
            <div>
              <label className="profile-field-label">Email (Google account — cannot change)</label>
              <Input value={profile.email || ""} disabled data-testid="profile-email-readonly-input" />
            </div>
            <div>
              <label className="profile-field-label">Workspace</label>
              <Input value={profile.workspace_domain || ""} disabled data-testid="profile-workspace-readonly-input" />
            </div>
          </div>

          <div className="profile-grid-two" data-testid="profile-whatsapp-grid">
            <div>
              <label className="profile-field-label">WhatsApp number (for task alerts)</label>
              <div className="profile-whatsapp-row">
                <Input value={profile.whatsapp_country_code || "+91"} onChange={(e) => setProfile((prev) => ({ ...prev, whatsapp_country_code: e.target.value }))} data-testid="profile-whatsapp-country-input" />
                <Input value={profile.whatsapp_number || ""} onChange={(e) => setProfile((prev) => ({ ...prev, whatsapp_number: e.target.value }))} data-testid="profile-whatsapp-number-input" />
              </div>
            </div>
            <div>
              <label className="profile-field-label">Birthday (for birthday leave)</label>
              <Input type="date" value={profile.birthday || ""} onChange={(e) => setProfile((prev) => ({ ...prev, birthday: e.target.value }))} data-testid="profile-birthday-input" />
              <div className="profile-field-note" data-testid="profile-birthday-note">Only HR and Super Admins can see this.</div>
            </div>
          </div>

          <div>
            <label className="profile-field-label">Company join date (when you actually joined Dimerse — used for work anniversary)</label>
            <Input type="date" value={profile.company_joined_at || ""} onChange={(e) => setProfile((prev) => ({ ...prev, company_joined_at: e.target.value }))} data-testid="profile-company-joined-input" />
            <div className="profile-field-note" data-testid="profile-company-joined-note">
              This is not your CRM account creation date. It is used to calculate work anniversaries on Team Calendar.
            </div>
          </div>
        </section>

        <section className="profile-card" data-testid="profile-role-access-card">
          <div className="profile-card-head">
            <div className="profile-card-title"><Shield className="h-4 w-4" /> Role &amp; access</div>
          </div>

          <div className="profile-role-grid" data-testid="profile-role-access-grid">
            <div>
              <div className="profile-field-label">Your role</div>
              <span className="chip chip-ok" data-testid="profile-role-chip">{roleLabelMap[user.role] || user.role}</span>
            </div>
            <div>
              <div className="profile-field-label">Leave approver</div>
              <div className="profile-field-value" data-testid="profile-leave-approver-label">
                {profile.leave_approver_email === user.email
                  ? "Self (you are your own approver)"
                  : profile.leave_approver_email || "Not assigned"}
              </div>
            </div>
            <div>
              <div className="profile-field-label">Company since</div>
              <div className="profile-field-value" data-testid="profile-company-since-label">{tenureLabel(profile.company_joined_at || user.created_at)}</div>
            </div>
          </div>

          <ul className="profile-role-permission-list" data-testid="profile-role-permission-list">
            {rolePermissionRows.map((item) => (
              <li key={item} data-testid={`profile-role-permission-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{item}</li>
            ))}
          </ul>

          <button type="button" className="profile-inline-link" onClick={() => setIsRoleRequestOpen(true)} data-testid="profile-open-role-request-button">
            Send a role change request →
          </button>
        </section>

        <section className="profile-card" data-testid="profile-account-card">
          <div className="profile-card-head">
            <div className="profile-card-title"><CalendarDays className="h-4 w-4" /> Account</div>
          </div>
          <p className="profile-field-note">Sign out of Dimerse CRM on this device. Your Google account is not affected.</p>
          <Button variant="outline" onClick={onLogout} data-testid="profile-signout-button">Sign out</Button>
        </section>
      </ProfileWorkspaceLayout>

      <Dialog open={isRoleRequestOpen} onOpenChange={setIsRoleRequestOpen}>
        <DialogContent data-testid="profile-role-request-modal">
          <DialogHeader>
            <DialogTitle data-testid="profile-role-request-modal-title">Send role change request</DialogTitle>
            <DialogDescription data-testid="profile-role-request-modal-subtitle">
              This request goes to super admins and appears in HR Manager View.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="profile-field-label">Requested role</label>
              <Select value={requestedRole} onValueChange={setRequestedRole}>
                <SelectTrigger data-testid="profile-role-request-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {requestableRoles.filter((item) => item !== user.role).map((item) => (
                    <SelectItem key={item} value={item} data-testid={`profile-role-request-option-${item}`}>
                      {roleLabelMap[item] || item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="profile-field-label">Why do you need this role?</label>
              <Textarea value={requestReason} onChange={(e) => setRequestReason(e.target.value)} rows={5} data-testid="profile-role-request-reason-input" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRoleRequestOpen(false)} data-testid="profile-role-request-cancel-button">Cancel</Button>
            <Button onClick={submitRoleRequest} disabled={isSubmittingRequest} data-testid="profile-role-request-submit-button">
              {isSubmittingRequest ? "Submitting..." : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
